import React from "react";

import { UI_COLORS, OVERLAY_COLORS, strings } from "@perceptron/constants";
import { Icon } from "@perceptron/ui/components/icon";

import { MarkupMode } from "../types";

import { ToolbarButton } from "./ToolbarButton";

interface AnnotationToolbarProps {
  isAnnotating: boolean;
  onToggleAnnotating: () => void;
  markupMode: MarkupMode;
  onMarkupModeChange: (mode: MarkupMode) => void;
  canUndo: boolean;
  onUndo: () => void;
  isDrawing?: boolean;
  selectedId?: string | null;
  onDelete?: (id: string) => void;
  onRename?: () => void;
}

export function MarkupToolbar({
  isAnnotating,
  onToggleAnnotating,
  markupMode,
  onMarkupModeChange,
  canUndo,
  onUndo,
  isDrawing = false,
  selectedId,
  onDelete,
  onRename,
}: AnnotationToolbarProps) {
  return (
    <div
      className="absolute bottom-0 left-0 right-0 z-30 h-[70px]"
      style={{ pointerEvents: isDrawing ? "none" : "auto" }}
    >
      <div className="flex h-full items-center justify-between px-2">
        {/* EditorAnnotation mode buttons - only shown when annotating */}
        <div className="flex items-center gap-2">
          {isAnnotating && (
            <>
              <ToolbarButton
                active={markupMode === MarkupMode.Point}
                icon={<Icon alt="Point" className="size-6 md:size-4" name="crosshair" />}
                label={strings.common.point}
                onClick={() => {
                  // Toggle between Point and Selection mode
                  if (markupMode === MarkupMode.Point) {
                    onMarkupModeChange(MarkupMode.Selection);
                  } else {
                    onMarkupModeChange(MarkupMode.Point);
                  }
                }}
              />

              <ToolbarButton
                active={markupMode === MarkupMode.Box}
                icon={<Icon className="size-6 md:size-4" name="box-icon" />}
                label={strings.imageDialog.toolbar.boundingBox}
                onClick={() => {
                  // Toggle between Box and Selection mode
                  if (markupMode === MarkupMode.Box) {
                    onMarkupModeChange(MarkupMode.Selection);
                  } else {
                    onMarkupModeChange(MarkupMode.Box);
                  }
                }}
              />

              {canUndo && (
                <>
                  <div className="h-6 w-px bg-white/30" />
                  <ToolbarButton
                    icon={<Icon alt="Undo" className="size-6 md:size-4" name="undo" />}
                    label={strings.common.undo}
                    onClick={onUndo}
                  />
                </>
              )}

              {/* Mobile-only buttons for selected annotation */}
              {selectedId && (
                <>
                  <div className="h-6 w-px bg-white/30 sm:hidden" />
                  <div className="flex items-center gap-2 sm:hidden">
                    <ToolbarButton
                      icon={<Icon alt="Rename" className="size-6 md:size-4" name="pencil-line" />}
                      label={strings.imageDialog.toolbar.rename}
                      onClick={() => onRename?.()}
                    />
                    <ToolbarButton
                      icon={<Icon alt="Remove" className="size-6 md:size-4" name="trash-2" />}
                      label={strings.imageDialog.toolbar.remove}
                      onClick={() => onDelete?.(selectedId)}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Toggle annotation mode button */}
        <button
          className="rounded-lg px-4 py-2 text-sm font-normal leading-5 text-white transition-colors focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-white"
          style={{
            backgroundColor: UI_COLORS.EDITOR_TOOLBAR_BG,
            boxShadow: `0px 4px 4px ${OVERLAY_COLORS.OVERLAY_MEDIUM}`,
          }}
          onClick={onToggleAnnotating}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = UI_COLORS.EDITOR_TOOLBAR_HOVER;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = UI_COLORS.EDITOR_TOOLBAR_BG;
          }}
          onTouchEnd={(e) => {
            // Handle the action directly on touchEnd to avoid iOS double-tap issue
            // This ensures the action happens on the first tap
            if (e.changedTouches.length > 0) {
              e.preventDefault(); // Prevent any default touch behavior
              onToggleAnnotating();
            }
          }}
        >
          {isAnnotating ? strings.common.done : strings.common.annotate}
        </button>
      </div>
    </div>
  );
}
